/*
  Carry Hope - "More" menu helper for Horizon's header menu (add-on, Oct 2026).
  Loaded by sections/carry-hope-brand-header.liquid. Does not modify any Horizon file.

  When some header links are tucked under "More" (narrow desktop widths, 768-1124px today):
    1. The tucked links open directly under More (Horizon puts them at the far-left edge).
    2. The menu stays open while the pointer travels from More to any tucked link at any angle (and back).
    3. A mouse click or tap on More never shuts a menu that is already open. Keyboard Enter/Space still
       opens and closes it (the theme's own toggle), Escape still closes it.
    4. It closes when the pointer is really somewhere else: outside the area between More and its links, or
       resting on another header control (logo, another link, search, cart). A click or tap outside closes it.
  Horizon detail: while the pointer moves, Horizon stretches an invisible "safety box" from the open item across
  the header row and only reports the pointer leaving after it pauses, so what is "under the pointer" is
  unreliable. This script judges position from element boxes only.
  Only active while this script marks the menu with data-ch-more-compact: More is showing, links are tucked
  under it, and none of them has its own submenu. If Horizon's header markup changes and the expected pieces
  are missing, this script does nothing and the theme behaves exactly as it ships.
*/
(() => {
  if (window.carryHopeMoreMenu) return;

  const ATTR = 'data-ch-more-compact';
  const SLACK = 6; // px of forgiveness around More and around the list of tucked links
  const DWELL = 300; // ms resting on another header control (logo, link, search, cart) before the menu closes
  let armed = false; // the pointer has been on More or its menu since the menu opened
  let bypass = false; // our own synthetic events must reach the theme untouched
  let dwell = 0;
  const pt = { x: -1, y: -1 };
  const watched = new WeakSet();

  const findMenu = () => document.querySelector('header-component header-menu') || document.querySelector('header-menu');

  function parts() {
    const menu = findMenu();
    const list = menu && menu.querySelector('overflow-list[ref="overflowMenu"]');
    const root = list && list.shadowRoot;
    const moreItem = list && list.querySelector(':scope > .menu-list__list-item[slot="more"]');
    const moreButton = moreItem && moreItem.querySelector('[ref="menuitem"]');
    const panel = root && root.querySelector('[part="overflow"]');
    const links = root && root.querySelector('[part="overflow-list"]');
    if (!menu || !list || !root || !moreItem || !moreButton || !panel || !links) return null;
    return { menu, list, root, moreItem, moreButton, panel, links };
  }

  const on = (e) => e.list.hasAttribute(ATTR);
  const isOpen = (e) => e.menu.dataset.overflowExpanded === 'true';
  const px = (v) => parseFloat(v) || 0;

  /* ---------- 1. place the tucked links under More ---------- */
  function place() {
    const e = parts();
    if (!e) return;
    watch(e);
    const tucked = [...e.list.querySelectorAll(':scope > .menu-list__list-item[slot="overflow"]')];
    const moreShown = e.moreButton.getClientRects().length > 0 && e.moreButton.offsetWidth > 0;
    const plain = !tucked.some((li) => li.querySelector('[ref="submenu[]"], .menu-list__submenu'));
    if (!moreShown || !tucked.length || !plain) {
      if (on(e)) {
        e.list.removeAttribute(ATTR);
        e.list.style.removeProperty('--ch-more-x');
      }
      return;
    }
    if (!on(e)) e.list.setAttribute(ATTR, '');

    const title = e.moreButton.querySelector('.menu-list__link-title') || e.moreButton;
    const textLeft = title.getBoundingClientRect().left + px(getComputedStyle(title).paddingLeft);
    const lr = e.links.getBoundingClientRect();
    const padLeft = px(getComputedStyle(e.links).paddingLeft);
    const pr = e.panel.getBoundingClientRect();
    const ps = getComputedStyle(e.panel);
    const minLeft = pr.left + px(ps.paddingLeft);
    const maxRight = Math.min(pr.right - px(ps.paddingRight), document.documentElement.clientWidth);
    // Line the link text up with the word "More"; slide left only if the list would run off the page.
    const left = Math.max(minLeft, Math.min(textLeft - padLeft, maxRight - lr.width));
    const cur = px(e.list.style.getPropertyValue('--ch-more-x'));
    const next = Math.round(cur + (left - lr.left));
    if (next !== cur) e.list.style.setProperty('--ch-more-x', `${next}px`);
  }

  let raf = 0;
  const schedule = () => {
    if (!raf) raf = requestAnimationFrame(() => { raf = 0; place(); });
  };

  function watch(e) {
    if (watched.has(e.list)) return;
    watched.add(e.list);
    // Horizon re-slots links between the bar and More as the width changes.
    new MutationObserver(schedule).observe(e.list, { subtree: true, attributes: true, attributeFilter: ['slot'] });
    new MutationObserver(schedule).observe(e.root, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
  }

  /* ---------- 2. geometry (element boxes only) ---------- */
  const within = (r, x, y) => x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;

  // 'more' = on More (down to the top of its links), 'menu' = on the open menu panel (it starts where the links
  // start; the strip between the bottom of More and the panel is still the header row), otherwise 'outside'.
  function zone(e, x, y) {
    const m = e.moreItem.getBoundingClientRect();
    const l = e.links.getBoundingClientRect();
    const p = e.panel.getBoundingClientRect();
    if (x >= m.left - 1 && x <= m.right + 1 && y >= m.top - 1 && y <= Math.max(m.bottom, l.top) + 1) return 'more';
    if (x >= p.left && x <= p.right && y >= l.top + 2 && y <= l.bottom + 1) return 'menu';
    return 'outside';
  }

  const corners = (r) => [
    [r.left - SLACK, r.top - SLACK], [r.right + SLACK, r.top - SLACK],
    [r.right + SLACK, r.bottom + SLACK], [r.left - SLACK, r.bottom + SLACK],
  ];

  function hull(points) {
    const p = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const half = (pts) => {
      const h = [];
      for (const q of pts) {
        while (h.length >= 2 && cross(h[h.length - 2], h[h.length - 1], q) <= 0) h.pop();
        h.push(q);
      }
      h.pop();
      return h;
    };
    return half(p).concat(half(p.slice().reverse()));
  }

  function inside(poly, x, y) {
    let sign = 0;
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const c = (b[0] - a[0]) * (y - a[1]) - (b[1] - a[1]) * (x - a[0]);
      if (c !== 0) {
        const s = c > 0 ? 1 : -1;
        if (!sign) sign = s;
        else if (s !== sign) return false;
      }
    }
    return true;
  }

  // The area between More and its links (convex hull of both boxes): any path from one to the other stays inside.
  const inCorridor = (e, x, y) =>
    inside(hull([...corners(e.moreItem.getBoundingClientRect()), ...corners(e.links.getBoundingClientRect())]), x, y);

  // Is the pointer on another clickable header control (logo, a bar link, search, account, cart)?
  function onControl(e, x, y) {
    const scope = e.menu.closest('header-component') || document.body;
    for (const el of scope.querySelectorAll('a[href], button, input, summary, [role="button"]')) {
      if (e.moreItem.contains(el) || el.closest('[slot="overflow"]')) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && within(r, x, y)) return true;
    }
    return false;
  }

  function hitAt(x, y) {
    let el = document.elementFromPoint(x, y);
    for (let i = 0; el && el.shadowRoot && i < 5; i++) {
      const inner = el.shadowRoot.elementFromPoint(x, y);
      if (!inner || inner === el) break;
      el = inner;
    }
    return el;
  }

  /* ---------- closing through the theme's own handlers ---------- */
  function clearDwell() {
    if (dwell) clearTimeout(dwell);
    dwell = 0;
  }

  function close(e) {
    armed = false;
    clearDwell();
    if (!isOpen(e)) return;
    bypass = true;
    try {
      e.moreItem.dispatchEvent(new PointerEvent('pointerleave', { bubbles: false, relatedTarget: document.body }));
      if (isOpen(e) && e.moreButton.getAttribute('aria-expanded') === 'true' && typeof e.menu.toggle === 'function') {
        e.menu.toggle({ target: e.moreButton }); // theme's forced close; focus stays where it is
      }
      if (isOpen(e)) e.menu.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    } finally {
      bypass = false;
    }
  }

  // After closing because the pointer went elsewhere, let the top-level link it is on react normally.
  function handBack(e) {
    const el = hitAt(pt.x, pt.y);
    const li = el && e.list.contains(el) ? el.closest('.menu-list__list-item') : null;
    if (li && li !== e.moreItem && li.slot !== 'overflow') {
      bypass = true;
      try { li.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false })); } finally { bypass = false; }
    }
  }

  /* ---------- event handlers (window capture: they run before the theme's own) ---------- */
  function onMove(ev) {
    if (ev.pointerType === 'touch') return;
    pt.x = ev.clientX;
    pt.y = ev.clientY;
    const menu = findMenu();
    if (!menu || menu.dataset.overflowExpanded !== 'true') {
      armed = false;
      clearDwell();
      return;
    }
    const e = parts();
    if (!e || !on(e)) return;
    if (zone(e, pt.x, pt.y) !== 'outside') {
      armed = true;
      clearDwell();
      return;
    }
    if (!armed) return; // opened from the keyboard with the mouse elsewhere: the theme decides
    if (!inCorridor(e, pt.x, pt.y)) {
      close(e);
      handBack(e);
      return;
    }
    if (!onControl(e, pt.x, pt.y)) {
      clearDwell();
      return;
    }
    if (!dwell) {
      dwell = setTimeout(() => {
        dwell = 0;
        const e2 = parts();
        if (e2 && armed && on(e2) && isOpen(e2) && zone(e2, pt.x, pt.y) === 'outside' && onControl(e2, pt.x, pt.y)) {
          close(e2); // resting on search, cart, the logo or another link: the visitor wants that instead
          handBack(e2);
        }
      }, DWELL);
    }
  }

  function onLeave(ev) {
    if (bypass) return;
    const t = ev.composedPath ? ev.composedPath()[0] : ev.target;
    if (!(t instanceof Element) || !(t.matches('[part="overflow"]') || t.matches('.menu-list__list-item[slot="more"]'))) return;
    const e = parts();
    if (!e || !on(e) || !isOpen(e) || (t !== e.moreItem && t !== e.panel)) return;
    const x = ev.clientX;
    const y = ev.clientY;
    const z = zone(e, x, y);
    if (t === e.moreItem && z === 'menu') return; // theme keeps it open while its menu is hovered
    if (z !== 'outside' || inCorridor(e, x, y)) {
      ev.stopImmediatePropagation(); // still on More, its menu, or on the way between them: keep it open
      armed = true;
    }
  }

  function onEnter(ev) {
    if (bypass) return;
    const t = ev.target;
    if (!(t instanceof Element) || !t.matches('.menu-list__list-item')) return;
    if (t.slot === 'more') place(); // before the theme measures and opens the menu
    const e = parts();
    if (!e || !on(e) || !isOpen(e) || !e.list.contains(t)) return;
    if (t === e.moreItem || t.slot === 'overflow' || !armed) return;
    // Another top-level link brushed on the way from More to its menu: ignore it while still on the way.
    const x = ev.isTrusted ? ev.clientX : pt.x;
    const y = ev.isTrusted ? ev.clientY : pt.y;
    if (inCorridor(e, x, y)) ev.stopImmediatePropagation();
  }

  function onClick(ev) {
    const e = parts();
    if (!e || !ev.composedPath().includes(e.moreButton)) return;
    place();
    if (!on(e)) return;
    const keyboard = ev.detail === 0 && !ev.pointerType;
    if (!keyboard && isOpen(e)) ev.stopImmediatePropagation(); // already open from hover/tap: leave it open
  }

  function onDown(ev) {
    const e = parts();
    if (!e || !on(e) || !isOpen(e)) return;
    const path = ev.composedPath();
    if (path.includes(e.moreItem) || path.includes(e.panel)) return;
    close(e); // tap or click outside More and its menu
  }

  function onOut(ev) {
    if (!armed || ev.relatedTarget || ev.pointerType === 'touch') return;
    const e = parts();
    if (e && on(e) && isOpen(e)) close(e); // pointer left the browser window
  }

  function onFocusIn(ev) {
    const e = parts();
    if (e && ev.composedPath().includes(e.moreButton)) place();
  }

  function attachPanelListener() {
    const e = parts();
    if (e && !watched.has(e.root)) {
      watched.add(e.root);
      // In case pointerleave from inside the menu's shadow DOM does not reach window.
      e.root.addEventListener('pointerleave', onLeave, true);
    }
  }

  window.carryHopeMoreMenu = { place, version: '2026-10-04d' };
  window.addEventListener('pointermove', onMove, { capture: true, passive: true });
  window.addEventListener('pointerleave', onLeave, true);
  window.addEventListener('pointerenter', onEnter, true);
  window.addEventListener('click', onClick, true);
  window.addEventListener('pointerdown', onDown, true);
  window.addEventListener('pointerout', onOut, true);
  window.addEventListener('focusin', onFocusIn, true);
  window.addEventListener('resize', schedule, { passive: true });
  const init = () => { attachPanelListener(); schedule(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('load', init);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
})();
